import React, { FC, Suspense } from "react";
import Stack from "@mui/material/Stack";
import { Tooltip, Typography } from "@mui/material";
import { isNotNil } from "ramda-adjunct";
import { useTranslation } from "react-i18next";
import { MdsIcon } from "@sinch/components";
import { MdsTimerOffXs } from "@sinch/icons";
import { ReadOnlyInputStatusChip } from "@sinch/components/application/RateInterval/ReadOnlyInputStatusChip";
import { MinUnitsLabel } from "@sinch/components/application/RateInterval/MinUnitsLabel";
import { RateItemSchemaFormFieldsInfer } from "@sinch/components/application/RatelistBuilder/ratelistBuilderSchema";
import { useUnitType } from "@sinch/components/application/Rate/useUnitType";
import { useRateInterval } from "@sinch/components/application/RateInterval/UseRateInterval";
import { useRateRounding } from "@sinch/components/application/RateRounding/useRateRounding";
import { useRoundingLabels } from "@sinch/components/application/RateRounding/useRoundingLabels";
import { usePriority } from "@sinch/components/application/common/usePriority";
import { useRateBuilderSettings } from "@sinch/components/application/RatelistBuilder/UtilitiesProviders/RateBuilderSettingsProvider";

export const RateUnitOverview: FC<{ rate: RateItemSchemaFormFieldsInfer["rate"] }> = ({ rate }) => {
  const { t } = useTranslation();
  const { rateType } = useRateBuilderSettings();
  const { getUnitTypeLabel } = useUnitType(rateType);
  const { getLabel } = useRateInterval();
  const { getColorForCustomPriority, getPriorityColor, getPriorityLabel, isNamedPriority } = usePriority();
  const { getRounding } = useRateRounding();
  const { formatRounding, formatRoundingCompact } = useRoundingLabels();

  if (!rate?.unit.type) {
    return null;
  }

  const unitType = rate.unit.type;
  const rounding = getRounding(rate.roundingId);
  const minimumHours = rate.minimum?.amount;
  const priority = rate.unit.priority;
  const override = rate.minimum?.overrideWorkedHours;

  return (
    <Stack direction="row" spacing={0.5} sx={{ height: "100%", alignItems: "flex-end" }}>
      <Typography sx={{ textAlign: "left" }}>
        {getLabel(rate.unit.customIntervalId) ?? getUnitTypeLabel(unitType)}
      </Typography>
      {!!minimumHours && (
        <ReadOnlyInputStatusChip
          label={
            <Stack alignItems="center" direction="row" gap={0.5}>
              <Suspense>
                <MinUnitsLabel minimumHours={minimumHours} unitType={unitType} />
              </Suspense>
              {override && (
                <Tooltip title={t("RateBuilder.overrideActualWorkedTime")}>
                  <MdsIcon fontSize="xs" icon={MdsTimerOffXs} />
                </Tooltip>
              )}
            </Stack>
          }
        />
      )}
      {isNotNil(priority) && (
        <ReadOnlyInputStatusChip
          backgroundColor={isNamedPriority(priority) ? getPriorityColor(priority) : getColorForCustomPriority(priority)}
          label={
            isNamedPriority(priority) ? `${getPriorityLabel(priority)} (${priority})` : `${t("priority")}: ${priority}`
          }
        />
      )}
      {isNotNil(rounding) && (
        <Tooltip title={`${t("RateBuilder.rounding")}: ${rounding.name} (${formatRounding(rounding)})`}>
          <span>
            <ReadOnlyInputStatusChip label={formatRoundingCompact(rounding)} />
          </span>
        </Tooltip>
      )}
    </Stack>
  );
};
